import { apiClient } from "./apiClient";

export type AgentType = "FIELD" | "OPERATIONS" | "INTERNAL";
export type AgentDistribution = "PUBLIC" | "CUSTOM";
export type AgentCapability = "VIEW" | "INSIGHT" | "RECOMMEND" | "ACT";

export interface AgentScreenshot {
  file: string;
  thumb: string;
}

export interface AgentScheduledAction {
  name: string;
  url: string;
  description?: string;
  /** Cron hint from the manifest; the cadence used when autoSchedule is true. */
  schedule?: string;
  /** The agent wants this action scheduled at enrolment; its schedule is read-only for the org. */
  autoSchedule?: boolean;
  /** Whether the action's key is read-only. */
  readOnly?: boolean;
  /** How often a schemaVersion 2 descriptor wants it run; null on v1 (STIC-15903 R6). */
  cadence?:
    | "once"
    | "daily"
    | "weekly"
    | "monthly"
    | "hourly"
    | "every_10_minutes"
    | null;
  /** The access a schemaVersion 2 descriptor declares: read, write or a role name; null on v1. */
  role?: string | null;
}

export interface AgentCatalogItem {
  /** Immutable identifier — used as URL slug (route `/agents/:name`). Not for display. */
  name: string;
  /** Human-readable display title — what we show in the UI everywhere. */
  title: string;
  description: string;
  author: string;
  tags: string[];
  agentType: AgentType;
  distribution: AgentDistribution;
  capabilities: AgentCapability[];
  schedulableActions: AgentScheduledAction[];
  llmRuntime: boolean;
  iconColor?: string;
  screenshots: AgentScreenshot[];
  screenshotBaseUrl: string;
  url: string;
  status: string;
  updatedAt?: string;
  createdAt?: string;
  /**
   * Platform-computed. True when this OPERATIONS agent is shown to a system user
   * even though the target org's plan does not include operations agents (below
   * Professional) — the UI shows a "normally not included in the plan" warning.
   */
  planRestricted?: boolean;
  /**
   * Organisation that registered this agent; absent when Sensolus owns it.
   * Set by the agent manager — see STIC-15607.
   */
  ownedByOrgId?: number;
}

export interface AgentAccess {
  system: boolean;
  partnerIds: number[];
  orgIds: number[];
  emails: string[];
}

export interface AgentDetail extends AgentCatalogItem {
  access: AgentAccess;
  /** schemaVersion of the agent's descriptor; null when the manager has none cached. */
  descriptorSchemaVersion?: number | null;
  /**
   * Public half of the deploy keypair generated when an org registered this
   * agent. Not a secret — it is here so the Agent Library can show it again for
   * copy-into-GitHub. Absent for Sensolus-owned agents.
   */
  deployPublicKey?: string;
}

/**
 * Returned by org-scoped endpoints (`getAgentsForOrg`, `getMenuAgentsForOrg`).
 * Same shape as AgentDetail plus the platform-side enrollment id and favourite flags.
 */
export interface EnrolledAgent extends AgentDetail {
  id: number;
  favorite: boolean;
  favouriteForAll: boolean;
  /**
   * Who added this agent to the org, and when. Not the same as the inherited
   * `createdAt`, which is the agent's own creation date in the agent manager —
   * these two are about the enrolment.
   */
  enrolledByName?: string;
  enrolledAt?: string;
  /**
   * Name behind `ownedByOrgId`. Absent when Sensolus owns the agent — every
   * catalogue agent — because there is no organisation row to name.
   */
  ownedByOrgName?: string;
  /**
   * The org this enrolment belongs to — the platform's "Owned by" for an agent,
   * and a different thing from `ownedByOrgId` above (which records who registered
   * the agent with the manager). Set on every path, so the detail page renders
   * the same ownership line as the list row (STIC-15806).
   */
  organisationId?: number;
  organisationName?: string;
  organisationType?: string;
  organisationPartnerName?: string;
  organisationPartnerVariant?: string;
}

export default {
  getCatalog(agentType?: AgentType, orgId?: number | null) {
    const params: Record<string, unknown> = {};
    if (agentType) params.agent_type = agentType.toLowerCase();
    if (orgId != null) params.org = orgId;
    return apiClient.get<AgentCatalogItem[]>("/rest/agents/catalog", {
      params,
    });
  },
  /** All agents enrolled for the org (used by the admin table + AllAgentsPage). */
  getAgentsForOrg(orgId: number) {
    return apiClient.get<EnrolledAgent[]>(`/rest/agents/org/${orgId}`);
  },
  /** Agents the user should see in the sidebar (favouriteForAll OR user-favourited). */
  getMenuAgentsForOrg(orgId: number) {
    return apiClient.get<EnrolledAgent[]>(`/rest/agents/org/${orgId}/menu`);
  },
  /**
   * Usage statistics: the agent was launched (iframe page mounted) in this org.
   * Fire-and-forget; the catalogue and store pages must not call this.
   */
  trackAgentOpened(orgId: number, name: string) {
    return apiClient.post<void>(
      `/rest/agents/org/${orgId}/${encodeURIComponent(name)}/opened`
    );
  },
  /**
   * Enrol an agent — keyed by (orgId, agentName) because the enrolled-agent row
   * doesn't exist yet. All later mutations use the returned enrolled-agent id.
   */
  addAgentToOrg(orgId: number, name: string) {
    return apiClient.post<AgentDetail>(
      `/rest/agents/org/${orgId}/${encodeURIComponent(name)}`
    );
  },
  removeEnrolledAgent(enrolledAgentId: number) {
    return apiClient.delete(`/rest/agents/enrolled/${enrolledAgentId}`);
  },
  /** Toggle the per-user favourite. Returns `{ favorite: boolean }`. */
  toggleUserFavorite(enrolledAgentId: number) {
    return apiClient.post<{ favorite: boolean }>(
      `/rest/agents/enrolled/${enrolledAgentId}/favorite`
    );
  },
  /** Admin: set the org-wide favouriteForAll flag. */
  setFavoriteForAll(enrolledAgentId: number, value: boolean) {
    return apiClient.put<{ favouriteForAll: boolean }>(
      `/rest/agents/enrolled/${enrolledAgentId}/favorite-for-all`,
      undefined,
      { params: { value } }
    );
  },
  /**
   * Generic advanced-search endpoint used by the admin agents table.
   * Mirrors `biDashboardClient.search` — POSTs the SaveView params (including
   * `queryObjectType: "AGENT"`) to the platform's generic search endpoint.
   */
  search(params: Record<string, unknown>) {
    return apiClient.post(`/rest/search`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },
  /**
   * "All agents" card-grid search — mirrors `businessIntelligenceClient.searchAllDashboard`.
   * Server resolves the target org from the auth session; the UI does not pass orgId.
   */
  /**
   * Cross-level "All" list (STIC-15615 R7): every enrolment in the login's
   * management scope — SYSTEM: every org, PARTNER: its managed orgs. Same
   * DTO as getAgentsForOrg, plus organisationId/Name per row.
   */
  getAgents() {
    return apiClient.get<EnrolledAgent[]>(`/rest/agents/managed`);
  },
  searchAllAgents(params: Record<string, unknown>) {
    return apiClient.post(`/rest/agents/search/all`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },
};
